import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import { PricingData } from '@approot/models/strategy-payload';
import {
  AlertsService,
  LegService,
  PricingExtraService
} from '@approot/services';
import {
  exponentialDelay,
  shortUuid,
  sleep,
  SubscriptionSink,
  Utilities
} from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import copy from 'copy-to-clipboard';
import { fromEvent } from 'rxjs';

type VegaByBucketByCurrency = Record<string, Record<string, number>>;

interface VegaByBucketPayload {
  defaultQuoteCurrency: string;
  body: string;
  vegaByBucketByCurrency: VegaByBucketByCurrency;
  orderedTenors: string[];
}

@Component({
  selector: 'app-vega-by-bucket',
  templateUrl: './vega-by-bucket.component.html',
  styleUrls: ['./vega-by-bucket.component.scss']
})
export class VegaByBucketComponent implements OnInit, OnDestroy {
  @Output() vegaByBucketOpenChanged = new EventEmitter<boolean>();
  @Input() isModalOpened = false;
  @Input() legComponents: LegComponent[];
  activeCurrencyPairs: string[] = [];
  currencyPairSelected: string;
  currencySelected: string;
  fetchingResponse: boolean;
  errorMessage: string;
  pricingData: PricingData = {};
  vegaByBucketResponse: VegaByBucketPayload;
  vegaByBucketForm: FormGroup;
  private subSink = new SubscriptionSink();
  private websocketUUID = 'vega-by-bucket_' + shortUuid();

  constructor(
    private legService: LegService,
    private elementRef: ElementRef,
    private pricingExtraService: PricingExtraService,
    private webSocketService: GlobalWebsocketService,
    private alertService: AlertsService
  ) {}

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  ngOnInit() {
    this.vegaByBucketForm = new FormGroup({
      currencyPair: new FormControl(''),
      currency: new FormControl('')
    });

    this.vegaByBucketForm.get('currencyPair').valueChanges.subscribe(val => {
      this.onCurrencyPairChange(val);
      this.vegaByBucketForm.get('currency').setValue(null);
      this.vegaByBucketForm.get('currency').disable();
    });

    this.vegaByBucketForm.get('currency').valueChanges.subscribe(val => {
      if (this.vegaByBucketResponse) {
        this.currencySelected = val;
      }
    });
    this.legComponents.map(x => {
      // there can't be duplicates of each currencyPair, so check if it already exists in the array before adding it.
      if (
        x.form.get('cross') &&
        x.form.get('cross').value &&
        this.activeCurrencyPairs.indexOf(x.form.get('cross').value) === -1
      ) {
        this.activeCurrencyPairs.push(x.form.get('cross').value);
      }

      if (
        x.form.get('secondCross') &&
        x.form.get('secondCross').value &&
        this.activeCurrencyPairs.indexOf(x.form.get('secondCross').value) === -1
      ) {
        // also the second cross
        this.activeCurrencyPairs.push(x.form.get('secondCross').value);
      }
    });

    this.listenToWebSocket();

    if (this.activeCurrencyPairs.length === 1) {
      // if only one currencyPair then just make the default value be the value available
      this.vegaByBucketForm
        .get('currencyPair')
        .setValue(this.activeCurrencyPairs[0]);
    }

    this.subSink.sink = fromEvent(document, 'keydown').subscribe(event => {
      // we want to listen to the f9 button press
      this.keypress(event);
    });
  }

  private listenToWebSocket(): void {
    const resubscribe = (iteration: number) => {
      if (iteration > 5) {
        this.alertService.addAlert({
          message:
            'There was a problem setting up the websocket for the vega by bucket',
          type: 'error'
        });
        return;
      }
      this.fetchingResponse = false;
      this.subSink.sink = this.webSocketService
        .getListener({
          action: `${API_PREFIX_V2}pricing`,
          uuid: this.websocketUUID,
          take: Infinity
        })
        .subscribe({
          next: resp => {
            this.fetchingResponse = false;
            if (resp.error) {
              this.alertService.addAlert({
                message: resp.error.message,
                type: 'error'
              });
              this.dataError();
            } else {
              this.handleWebSocketPayload(resp.payload as VegaByBucketPayload);
            }
          },
          complete: async () => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          },
          error: async _ => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private handleWebSocketPayload(payload: VegaByBucketPayload): void {
    if (payload.vegaByBucketByCurrency) {
      this.vegaByBucketResponse = payload;
      this.vegaByBucketForm.get('currency').enable();
      this.vegaByBucketForm
        .get('currency')
        .setValue(payload.defaultQuoteCurrency);
    } else if (payload.body) {
      this.alertService.addAlert({
        message: payload.body,
        type: 'error'
      });
      this.dataError();
    }
  }

  keypress(event) {
    if (event.keyCode === 120 && !this.fetchingResponse) {
      // if the key is f9 and the forms are valid, calculate the value
      this.onCurrencyPairChange(this.currencySelected);
    }
  }

  dataError() {
    this.fetchingResponse = false;
    this.errorMessage = 'Failed to load data';
  }

  modalChanged(event) {
    this.vegaByBucketOpenChanged.emit(event);
  }

  formatTableForCopy() {
    const table = this.elementRef.nativeElement.querySelector('#table-data');

    return Utilities.tableToExcelFormat(table);
  }

  onCopyToClipboardClicked() {
    copy(this.formatTableForCopy(), { format: 'text/plain' }); // copy the text. keep in text/plain, so that tabs and new lines stay
  }
  onCurrencyPairChange(currencyPair: string) {
    this.currencyPairSelected = currencyPair;
    if (currencyPair) {
      const cross =
        currencyPair.substring(0, 3) + '/' + currencyPair.substring(3, 6);
      let task = {
        taskType: 'vegaByBucket',
        taskParameters: {
          cross
        }
      };

      //join all the market data from all the legs
      this.pricingData = {
        fixings: [],
        treasuryRates: [],
        marketData: []
      };

      // get fixings
      let filteredRequiredFixings = [];

      this.legComponents.forEach((legComponent: LegComponent) => {
        if (legComponent.legHelperService.requiredFixings) {
          let fixings = legComponent.legPricingService.getFixings();
          filteredRequiredFixings = fixings.concat(fixings);
        }
      });

      this.pricingData.fixings = filteredRequiredFixings;

      // get treasury curves
      this.price(task);
    } else {
      this.vegaByBucketResponse = null;
    }
  }

  price(task) {
    // add all of the selected legs
    let payload = this.legService.createServicePayload(
      this.pricingExtraService.getMarketDataTimeStamp,
      this.pricingExtraService.marketDataSpreadingRule,
      this.legComponents.map(y => {
        return { product: y.form.value, attributes: y.leg.attributes };
      }),
      task,
      this.pricingData
    );

    this.fetchingResponse = true; // make the spinner show
    this.errorMessage = ''; // wipe these so a message and no data appears
    this.vegaByBucketResponse = null;
    this.webSocketService.sendMessage({
      action: `${API_PREFIX_V2}pricing`,
      payload,
      uuid: this.websocketUUID
    });
  }
}
